import React, { useEffect, useMemo, useState } from 'react';
import api from '../../lib/api';
import { Loader2, Plus, Pencil, Trash2, Eye, EyeOff, X, CheckSquare, Square } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from '../../hooks/use-toast';

const emptyCategory = { name: '', visible: true, icon: '', emoji: '', order: 0 };

const CategoriesSection = () => {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [modal, setModal] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [busyBulk, setBusyBulk] = useState(false);

  const load = () => {
    setLoading(true);
    api.get('/admin/categories').then((r) => {
      setCats(r.data);
      setSelected(new Set());
    }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const allSlugs = useMemo(() => cats.map((c) => c.slug), [cats]);
  const allSelected = allSlugs.length > 0 && allSlugs.every((s) => selected.has(s));
  const someSelected = selected.size > 0 && !allSelected;

  const toggleOne = (slug) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug); else next.add(slug);
      return next;
    });
  };
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(allSlugs));

  const openNew = () => { setEditing({ ...emptyCategory }); setModal(true); };
  const openEdit = (c) => { setEditing({ ...c }); setModal(true); };

  const toggleVisible = async (cat) => {
    try {
      await api.patch(`/admin/categories/${cat.slug}`, { ...cat, visible: !cat.visible });
      toast({ title: !cat.visible ? 'Categoría visible' : 'Categoría oculta' });
      load();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'No se actualizó' });
    }
  };

  const save = async () => {
    if (!editing.name.trim()) { toast({ title: 'Nombre requerido' }); return; }
    const payload = {
      name: editing.name.trim(),
      visible: !!editing.visible,
      icon: editing.icon || null,
      emoji: editing.emoji || null,
      order: Number(editing.order) || 0,
    };
    try {
      if (editing.slug) {
        await api.patch(`/admin/categories/${editing.slug}`, payload);
        toast({ title: 'Categoría actualizada' });
      } else {
        await api.post('/admin/categories', payload);
        toast({ title: 'Categoría creada' });
      }
      setModal(false);
      load();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'No se guardó' });
    }
  };

  const remove = async (cat) => {
    if (!window.confirm(`¿Eliminar la categoría "${cat.name}"?`)) return;
    try {
      await api.delete(`/admin/categories/${cat.slug}`);
      toast({ title: 'Categoría eliminada' });
      load();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'No se pudo eliminar' });
    }
  };

  const bulk = async (action) => {
    const slugs = Array.from(selected);
    if (!slugs.length) return;
    const labels = { delete: `eliminar ${slugs.length} categoría${slugs.length === 1 ? '' : 's'}`, hide: `ocultar ${slugs.length} categoría${slugs.length === 1 ? '' : 's'}`, show: `mostrar ${slugs.length} categoría${slugs.length === 1 ? '' : 's'}` };
    if (action === 'delete' && !window.confirm(`¿Estás seguro de ${labels.delete}? Solo se eliminarán las que no tengan productos asignados.`)) return;
    setBusyBulk(true);
    try {
      const r = await api.post('/admin/categories/bulk', { action, slugs });
      const done = r.data?.deleted ?? r.data?.updated ?? 0;
      const blocked = r.data?.blocked || [];
      if (action === 'delete' && blocked.length) {
        toast({ title: `Eliminadas ${done}`, description: `${blocked.length} categoría(s) tienen productos y no se borraron. Reasígnalos primero.` });
      } else {
        toast({ title: 'Listo', description: `Se aplicó "${labels[action]}" (${done}).` });
      }
      load();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'No se pudo aplicar' });
    } finally {
      setBusyBulk(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-black text-white">Categorías</h1>
          <p className="text-white/50 text-sm mt-1">{cats.length} categorías registradas {selected.size > 0 && <span className="text-orange-400 font-semibold">· {selected.size} seleccionadas</span>}</p>
        </div>
        <Button onClick={openNew} className="bg-orange-500 hover:bg-orange-600 text-black font-bold shrink-0" data-testid="admin-cats-new">
          <Plus className="w-4 h-4 mr-1" /> Nueva
        </Button>
      </div>

      {/* Bulk actions bar */}
      {selected.size > 0 && (
        <div className="sticky top-2 z-20 mb-3 flex flex-wrap items-center gap-2 rounded-xl bg-orange-500/10 border border-orange-500/30 backdrop-blur-md px-3 py-2 shadow-lg" data-testid="admin-cats-bulkbar">
          <span className="text-orange-200 text-xs font-bold uppercase tracking-wide">
            {selected.size} seleccionada{selected.size === 1 ? '' : 's'}
          </span>
          <div className="flex items-center gap-1.5 ml-auto flex-wrap">
            <Button onClick={() => bulk('show')} disabled={busyBulk} className="h-8 px-3 rounded-full !bg-emerald-500/15 hover:!bg-emerald-500/25 border border-emerald-500/30 !text-emerald-300 text-xs font-bold" data-testid="admin-cats-bulk-show"><Eye className="w-3.5 h-3.5 mr-1" /> Mostrar</Button>
            <Button onClick={() => bulk('hide')} disabled={busyBulk} className="h-8 px-3 rounded-full !bg-amber-500/15 hover:!bg-amber-500/25 border border-amber-500/30 !text-amber-300 text-xs font-bold" data-testid="admin-cats-bulk-hide"><EyeOff className="w-3.5 h-3.5 mr-1" /> Ocultar</Button>
            <Button onClick={() => bulk('delete')} disabled={busyBulk} className="h-8 px-3 rounded-full !bg-rose-500/15 hover:!bg-rose-500/25 border border-rose-500/30 !text-rose-300 text-xs font-bold" data-testid="admin-cats-bulk-delete">
              {busyBulk ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Trash2 className="w-3.5 h-3.5 mr-1" />} Eliminar
            </Button>
            <button onClick={() => setSelected(new Set())} className="h-8 px-2 rounded-full text-white/50 hover:text-white hover:bg-white/10 text-xs" data-testid="admin-cats-bulk-clear"><X className="w-3.5 h-3.5" /></button>
          </div>
        </div>
      )}

      {loading ? <Loader2 className="w-6 h-6 animate-spin text-orange-500" /> : (
        <div className="bg-neutral-950 border border-white/10 rounded-2xl overflow-x-auto">
          <table className="w-full text-sm min-w-[560px]">
            <thead className="bg-white/5 text-white/60">
              <tr>
                <th className="text-left px-3 py-2 w-10">
                  <button
                    type="button"
                    onClick={toggleAll}
                    aria-label="Seleccionar todo"
                    className="inline-flex items-center justify-center w-5 h-5 rounded border border-white/20 hover:border-orange-400/60 hover:bg-orange-500/10 text-orange-300"
                    data-testid="admin-cats-select-all"
                  >
                    {allSelected ? <CheckSquare className="w-4 h-4" /> : someSelected ? <span className="w-2.5 h-0.5 bg-orange-400 rounded"></span> : <Square className="w-4 h-4 text-white/25" />}
                  </button>
                </th>
                <th className="text-left px-3 py-2">Nombre</th>
                <th className="text-left px-3 py-2">Emoji</th>
                <th className="text-left px-3 py-2">Slug</th>
                <th className="text-right px-3 py-2">Productos</th>
                <th className="text-right px-3 py-2">Orden</th>
                <th className="text-center px-3 py-2">Visible</th>
                <th className="text-right px-3 py-2 w-32">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {cats.map((c) => {
                const isSel = selected.has(c.slug);
                return (
                  <tr key={c.slug} className={`border-t border-white/5 hover:bg-white/5 ${isSel ? 'bg-orange-500/[0.06]' : ''}`}>
                    <td className="px-3 py-2">
                      <button
                        type="button"
                        onClick={() => toggleOne(c.slug)}
                        aria-label={`Seleccionar ${c.name}`}
                        className={`inline-flex items-center justify-center w-5 h-5 rounded border transition-colors ${isSel ? 'border-orange-400 bg-orange-500 text-black' : 'border-white/20 hover:border-orange-400/60 hover:bg-orange-500/10 text-white/40'}`}
                        data-testid={`admin-cat-select-${c.slug}`}
                      >
                        {isSel ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                      </button>
                    </td>
                    <td className="px-3 py-2 text-white font-semibold">{c.name}</td>
                    <td className="px-3 py-2 text-xl">{c.emoji || '—'}</td>
                    <td className="px-3 py-2 text-white/60 text-xs font-mono">{c.slug}</td>
                    <td className="px-3 py-2 text-right text-white/70">{c.count ?? 0}</td>
                    <td className="px-3 py-2 text-right text-white/70">{c.order ?? 0}</td>
                    <td className="px-3 py-2 text-center">
                      <button onClick={() => toggleVisible(c)} className={`w-8 h-8 rounded-md inline-flex items-center justify-center ${c.visible !== false ? 'bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20' : 'bg-white/5 text-white/40 hover:bg-white/10'}`} title={c.visible !== false ? 'Ocultar' : 'Mostrar'}>
                        {c.visible !== false ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </button>
                    </td>
                    <td className="px-3 py-2 text-right space-x-1 whitespace-nowrap">
                      <button onClick={() => openEdit(c)} className="w-8 h-8 rounded-md bg-white/5 hover:bg-white/10 text-white inline-flex items-center justify-center"><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => remove(c)} className="w-8 h-8 rounded-md bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 inline-flex items-center justify-center"><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                );
              })}
              {cats.length === 0 && (
                <tr><td colSpan={7} className="px-3 py-8 text-center text-white/50">Sin categorías todavía.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={modal} onOpenChange={setModal}>
        <DialogContent className="sm:max-w-md bg-neutral-950 border-white/10 text-white">
          <DialogHeader><DialogTitle className="text-white">{editing?.slug ? 'Editar categoría' : 'Nueva categoría'}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <label className="text-xs text-white/60">Nombre</label>
                <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="bg-white/5 border-white/10 text-white" placeholder="Ej. E-Juice" />
              </div>
              <div>
                <label className="text-xs text-white/60">Emoji</label>
                <div className="flex items-center gap-2">
                  <Input
                    value={editing.emoji || ''}
                    onChange={(e) => setEditing({ ...editing, emoji: e.target.value })}
                    className="bg-white/5 border-white/10 text-white text-xl w-24"
                    placeholder="🛒"
                    maxLength={4}
                    data-testid="admin-cat-emoji-input"
                  />
                  {editing.emoji && (
                    <span className="text-3xl leading-none">{editing.emoji}</span>
                  )}
                  <span className="text-white/40 text-xs">Escribe o pega un emoji</span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-white/60">Orden</label>
                  <Input type="number" value={editing.order} onChange={(e) => setEditing({ ...editing, order: e.target.value })} className="bg-white/5 border-white/10 text-white" />
                </div>
                <div className="flex items-end pb-1">
                  <label className="flex items-center gap-2 text-sm text-white">
                    <input type="checkbox" checked={editing.visible} onChange={(e) => setEditing({ ...editing, visible: e.target.checked })} /> Visible en el sitio
                  </label>
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setModal(false)} className="bg-transparent border-white/20 text-white hover:bg-white/10"><X className="w-4 h-4 mr-1" /> Cancelar</Button>
                <Button onClick={save} className="bg-orange-500 hover:bg-orange-600 text-black font-bold">Guardar</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CategoriesSection;
